import { motion, useInView } from "motion/react";
import { useRef } from "react";
import { CERTIFICATIONS } from "../../data/profileData";

/**
 * The badge wall. Sits directly under Capabilities so every claim in the skills
 * list is followed by something a hiring manager can click and verify.
 *
 * Artwork is self-hosted and each card is a plain link to the issuer's public
 * verification page — see the note on CERTIFICATIONS in data/profileData.ts for
 * why the Credly embed script is deliberately not used.
 */
export function CertificationsSection() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section ref={ref} className="relative w-full px-4 md:px-[116px] pb-16 md:pb-24">
      <div className="max-w-[1400px] mx-auto flex flex-col gap-8">
        <div className="flex flex-col gap-3">
          <p className="font-mono text-[12px] md:text-[14px] tracking-[3px] uppercase text-[#666]">Certifications</p>
          <h2 className="font-black text-[28px] md:text-[40px] leading-[1.1] tracking-tight text-[#141414]">
            Verified, not just claimed
          </h2>
          <p className="text-[16px] md:text-[17px] leading-[1.55] text-[#666] max-w-[640px]">
            Every badge below links to the issuer's own verification page — check any of them.
          </p>
        </div>

        <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CERTIFICATIONS.map((cert, i) => (
            <motion.li
              key={cert.name}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.08, ease: "easeOut" }}
            >
              <a
                href={cert.verifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex h-full flex-col items-center gap-4 rounded-[24px] border-2 border-[#141414] bg-white p-6 text-center shadow-[0px_8px_0px_-2px_rgba(0,62,91,0.18)] transition-transform duration-200 hover:-translate-y-1 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#003e5b]/30"
              >
                <img
                  src={cert.image}
                  alt={`${cert.name} badge, issued by ${cert.issuer}`}
                  width={112}
                  height={112}
                  loading="lazy"
                  decoding="async"
                  className="h-[112px] w-[112px] object-contain"
                />
                <span className="h-[5px] w-[32px] rounded-full" style={{ background: cert.accent }} aria-hidden="true" />
                <span className="flex flex-col gap-1">
                  <span className="font-bold text-[16px] leading-tight text-[#141414]">{cert.name}</span>
                  <span className="text-[14px] text-[#666]">{cert.issuer}</span>
                </span>
                <span className="mt-auto pt-2 font-mono text-[11px] tracking-[2px] uppercase text-[#003e5b] group-hover:underline underline-offset-4">
                  Verify →
                </span>
              </a>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}
